import { useEffect, useState } from "react";
import { api, formatApiError, formatIDR } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, Pencil, Trash2, Wrench } from "lucide-react";
import { toast } from "sonner";

const UNITS = ["pcs", "liter", "set", "box", "roll", "meter", "kg"];
const EMPTY = { name: "", unit: "pcs", default_price: 0, notes: "" };

export default function SparepartMaster() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/sparepart-catalog").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ name: r.name, unit: r.unit, default_price: r.default_price, notes: r.notes || "" }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing) {
        await api.patch(`/sparepart-catalog/${editing.id}`, form);
        toast.success("Master sparepart diperbarui");
      } else {
        await api.post("/sparepart-catalog", form);
        toast.success("Sparepart ditambahkan ke master");
      }
      setOpen(false);
      setEditing(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const del = async (r) => {
    if (!confirm(`Hapus ${r.name} dari master?`)) return;
    try { await api.delete(`/sparepart-catalog/${r.id}`); toast.success("Dihapus"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-black text-slate-900">Master Sparepart</h1>
          <p className="text-slate-500 text-sm mt-1">
            Daftar sparepart dengan satuan (liter, pcs, dst) dan harga standar. Koordinator hanya lihat nama & satuan.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openAdd} className="bg-orange-500 hover:bg-orange-600 text-slate-900 font-bold uppercase tracking-wider rounded-sm btn-industrial h-12" data-testid="add-sparepart-master-btn">
              <Plus className="w-5 h-5 mr-1" /> Tambah Sparepart
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm border-2 border-slate-900">
            <DialogHeader><DialogTitle className="font-display text-2xl font-black">{editing ? "Edit Master" : "Tambah Master Sparepart"}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label className="uppercase text-xs tracking-widest font-bold">Nama *</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-12 rounded-sm" placeholder="Oli Mesin SAE 40" data-testid="sm-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="uppercase text-xs tracking-widest font-bold">Satuan *</Label>
                  <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                    <SelectTrigger className="h-12 rounded-sm" data-testid="sm-unit"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="uppercase text-xs tracking-widest font-bold">Harga per {form.unit} *</Label>
                  <Input required type="number" min={0} value={form.default_price} onChange={(e) => setForm({ ...form, default_price: Number(e.target.value) })} className="h-12 rounded-sm font-mono" data-testid="sm-price" />
                </div>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={busy} data-testid="sm-submit" className="bg-orange-500 hover:bg-orange-600 text-slate-900 font-bold uppercase rounded-sm btn-industrial">
                  {busy ? "..." : editing ? "Simpan" : "Tambah"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border-2 border-slate-200 bg-white rounded-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-900 hover:bg-slate-900">
              <TableHead className="text-orange-400 uppercase text-xs tracking-widest font-bold">Nama</TableHead>
              <TableHead className="text-orange-400 uppercase text-xs tracking-widest font-bold">Satuan</TableHead>
              <TableHead className="text-orange-400 uppercase text-xs tracking-widest font-bold text-right">Harga / satuan</TableHead>
              <TableHead className="text-orange-400 uppercase text-xs tracking-widest font-bold w-24 text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`sm-row-${r.name}`}>
                <TableCell className="font-bold">{r.name}</TableCell>
                <TableCell className="font-mono uppercase text-xs">{r.unit}</TableCell>
                <TableCell className="text-right font-mono">{formatIDR(r.default_price)}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(r)}><Pencil className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => del(r)}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={4} className="text-center py-12 text-slate-500"><Wrench className="w-8 h-8 mx-auto mb-2 opacity-40" />Belum ada master sparepart.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
